import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Helmet } from 'react-helmet-async';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Music, Sparkles } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import SongGrid from '@/components/songs/SongGrid';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  software_type: string;
  holiday_type: string;
  featured: boolean;
  meta_title: string;
  meta_description: string;
  image_url: string | null;
  image_alt: string | null;
  created_at: string;
  updated_at: string;
}

interface CategorySong {
  id: string;
  title: string;
  artist: string | null;
  album: string | null;
  slug: string;
  album_cover_url: string | null;
  sequence_count: number;
  total_downloads: number;
  average_rating: number;
  spotify_preview_url: string | null;
}

export default function CategoryDetail() {
  const { slug } = useParams<{ slug: string }>();

  // Fetch category details
  const { data: category, isLoading: categoryLoading, error: categoryError } = useQuery({
    queryKey: ['category', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return data as Category;
    },
    enabled: !!slug,
  });

  // Fetch songs in this category
  const { data: songs = [], isLoading: songsLoading } = useQuery({
    queryKey: ['category-songs', category?.id],
    queryFn: async () => {
      if (!category?.id) return [];

      const { data, error } = await supabase
        .from('category_songs')
        .select(`
          sort_order,
          songs (
            id,
            title,
            artist,
            album,
            slug,
            album_cover_url,
            sequence_count,
            total_downloads,
            average_rating,
            spotify_preview_url
          )
        `)
        .eq('category_id', category.id)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data.map(item => item.songs).filter(Boolean) as CategorySong[];
    },
    enabled: !!category?.id,
  });

  const isLoading = categoryLoading || songsLoading;

  if (categoryError) {
    return (
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="flex-1">
          <div className="container mx-auto px-4 py-8">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {categoryError.message === 'JSON object requested, single row expected' 
                  ? 'Category not found' 
                  : 'Failed to load category details'}
              </AlertDescription>
            </Alert>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{category?.meta_title || (category ? `${category.name} | Light Hunt` : 'Loading...')}</title>
        {category && (
          <>
            <meta name="description" content={category.meta_description || category.description} />
            <meta property="og:title" content={category.meta_title || category.name} />
            <meta property="og:description" content={category.meta_description || category.description} />
          </>
        )}
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="flex-1 bg-background">
        {isLoading ? (
          <div className="container mx-auto px-4 py-8 space-y-8">
            {/* Loading skeleton for hero */}
            <div className="space-y-4">
              <Skeleton className="h-64 w-full rounded-lg" />
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
            
            {/* Loading skeleton for songs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-64 rounded-lg" />
              ))}
            </div>
          </div>
        ) : category ? (
          <>
            {/* Category Hero */}
            <div className="relative bg-linear-to-b from-primary/10 to-background">
              <div className="container mx-auto px-4 py-16 pt-24">
                <div className="flex items-start justify-between gap-8">
                  <div className="flex-1 max-w-4xl">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-lg px-4 py-2">
                          {category.software_type}
                        </Badge>
                        {category.featured && (
                          <Badge variant="default" className="text-lg px-4 py-2">
                            <Sparkles className="w-4 h-4 mr-2" />
                            Featured
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">
                      {category.name}
                    </h1>
                    
                    <p className="text-xl text-muted-foreground mb-6 max-w-2xl">
                      {category.description}
                    </p>

                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <Music className="w-5 h-5 text-muted-foreground" />
                        <span className="text-lg">
                          {songs.length} {songs.length === 1 ? 'Song' : 'Songs'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Category Image */}
                  {category.image_url && (
                    <div className="hidden md:block shrink-0">
                      <img 
                        src={category.image_url} 
                        alt={category.image_alt || category.name}
                        className="w-64 h-48 object-cover rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Songs Grid */}
            <div className="container mx-auto px-4 py-8">
              {songs.length > 0 ? (
                <>
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-2">
                      Songs in {category.name}
                    </h2>
                    <p className="text-muted-foreground">
                      Each song may have multiple sequences available for download
                    </p>
                  </div>
                  <SongGrid songs={songs} />
                </>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No songs in this category yet. Check back later for new additions!
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </>
        ) : null}
        </main>
        <Footer />
      </div>
    </>
  );
}